import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, MapPin, ChevronRight } from 'lucide-react';
import { useLang } from '../i18n';
import { useSeasons } from '../lib/useSeasons';
import { LOGO } from '../lib/api';

// Sei voci per chi arriva a cercare o a farsi ispirare, piu' quella che
// cambia col calendario, piu' Ho.Re.Ca in coda: promossa qui dalla fascia
// pro perche' quel pubblico ha bisogno di piu' visibilita' di quanta gliene
// desse una barra secondaria invisibile su mobile (decisione del 28/08/2026,
// eccezione consapevole al vincolo "nav a 5-6 voci" di overview.md). "Dove
// comprare" sta solo nella CTA. Le etichette riusano il titolo delle pagine
// (`catalog.title`, `stories.title`) invece di duplicarlo in chiavi `nav.*`
// gemelle; nel menu la voce "Componi" e' corta, perche' una barra di
// navigazione non e' il posto di una frase. Contatti in coda (feedback
// 02/11/2026): prima stava solo nel footer.
const NAV = (t, stagione) => [
  { to: '/catalogo', label: t('catalog.title') },
  { to: '/componi', label: t('nav.buildShort') },
  { to: '/collezioni', label: t('collections.title') },
  stagione,
  { to: '/storie', label: t('stories.title') },
  { to: '/azienda', label: t('nav.company') },
  { to: '/professionisti/horeca', label: t('nav.horeca'), id: 'horeca' },
  { to: '/contatti', label: t('nav.contact') },
].filter(Boolean);

/**
 * La voce della stagione: oggi dice «Natale», e cambia nome da sola il giorno
 * in cui il registro pubblica un'altra stagione. Il punto acceso c'e' solo
 * quando la stagione e' davvero in corso.
 */
const voceStagione = (voce) =>
  voce && {
    id: 'stagioni',
    to: voce.path,
    label: voce.title,
    accesa: voce.active,
  };

// Rivenditori e redazioni restano nella fascia pro: due pubblici diversi,
// due link espliciti. Ho.Re.Ca e' salita nella nav principale (vedi NAV
// sopra) per darle lo stesso peso delle voci consumer.
const B2B = (t) => [
  { to: '/professionisti/rivenditori', label: t('nav.reseller') },
  { to: '/area-stampa', label: t('nav.press') },
];

export const Header = () => {
  const { t, lang, setLang } = useLang();
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const menuRef = useRef(null);
  const triggerRef = useRef(null);

  const closeMenu = () => {
    setOpen(false);
    triggerRef.current?.focus();
  };

  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  // dialog aperto: manda focus dentro, Escape chiude, Tab resta chiuso nel menu
  useEffect(() => {
    if (!open) return;
    menuRef.current?.querySelector('[data-testid="menu-close-btn"]')?.focus();

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        closeMenu();
        return;
      }
      if (e.key !== 'Tab') return;
      const focusables = menuRef.current?.querySelectorAll('a[href], button:not([disabled])');
      if (!focusables?.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const { voce } = useSeasons();
  const nav = NAV(t, voceStagione(voce));
  const b2b = B2B(t);

  return (
    <>
      {/* fascia professionale: il B2B e' il modello di business */}
      <div className="hidden md:block bg-inchiostro text-carta" data-testid="header-pro-bar">
        <div className="wrap flex items-center justify-end gap-7">
          {b2b.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`pro-nav-${l.to.split('/').pop()}`}
              className={({ isActive }) =>
                `link-ul inline-flex min-h-[44px] items-center px-1 text-[14px] font-semibold transition-colors hover:text-ocra ${
                  isActive ? 'text-ocra' : 'text-carta/85'
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <LangSwitch lang={lang} setLang={setLang} light testid="lang-switch-pro" />
        </div>
      </div>

      <header
        data-testid="site-header"
        className="sticky top-0 z-[var(--z-sticky)] bg-carta"
      >
        <div className="wrap flex items-center justify-between gap-4 py-3 md:py-4">
          <Link to="/" data-testid="logo-link" aria-label="VdE Tivoli 1996" className="shrink-0">
            <img
              src={LOGO.minimal}
              alt="VdE Tivoli 1996, 30 anni"
              className="h-11 w-auto md:h-14"
              width="511"
              height="151"
            />
          </Link>

          <nav className="hidden xl:flex items-center gap-4 2xl:gap-6">
            {nav.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.id || l.to.replace('/', '')}`}
                className={({ isActive }) =>
                  `link-ul relative before:absolute before:-inset-y-[11px] before:inset-x-0 before:content-[''] whitespace-nowrap text-[14px] font-semibold transition-colors hover:text-marsala ${
                    isActive ? 'text-marsala' : 'text-inchiostro'
                  }`
                }
              >
                {l.accesa && <Acceso />}
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Link
              to="/dove-comprare"
              data-testid="header-cta-where"
              aria-label={t('common.findRetailer')}
              className="btn btn-solid hidden sm:inline-flex whitespace-nowrap !py-3 !px-4 !text-[14px] xl:!px-5"
            >
              <MapPin size={13} strokeWidth={2.2} />
              <span>{t('common.findRetailer')}</span>
            </Link>
            <button
              type="button"
              ref={triggerRef}
              onClick={() => setOpen(true)}
              data-testid="menu-open-btn"
              aria-label={t('nav.menu')}
              aria-expanded={open}
              aria-controls="mobile-menu-panel"
              data-ui
              className="xl:hidden grid h-11 w-11 place-items-center border border-inchiostro/20 transition-colors hover:bg-inchiostro hover:text-carta"
            >
              <Menu size={19} strokeWidth={1.6} />
            </button>
          </div>
        </div>
      </header>

      {open && (
        <div
          ref={menuRef}
          id="mobile-menu-panel"
          role="dialog"
          aria-modal="true"
          aria-label={t('nav.menu')}
          data-testid="mobile-menu"
          className="fixed inset-0 z-[var(--z-modal)] bg-carta overflow-y-auto animate-rise"
        >
          <div className="wrap flex items-center justify-between py-3">
            <img src={LOGO.minimal} alt="VdE Tivoli 1996, 30 anni" className="h-10 w-auto" width="511" height="151" />
            <button
              type="button"
              onClick={closeMenu}
              data-testid="menu-close-btn"
              aria-label={t('nav.close')}
              data-ui
              className="grid h-11 w-11 place-items-center border border-inchiostro/20"
            >
              <X size={19} strokeWidth={1.6} />
            </button>
          </div>

          <div className="wrap pb-16 pt-6">
            <ul className="mb-10">
              {nav.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    data-testid={`m-nav-${l.id || l.to.replace('/', '')}`}
                    className="flex items-center justify-between py-3 font-display text-2xl"
                  >
                    <span>
                      {l.accesa && <Acceso />}
                      {l.label}
                    </span>
                    <ChevronRight size={20} className="text-marsala" strokeWidth={1.4} />
                  </Link>
                </li>
              ))}
            </ul>

            <p className="mb-2 text-[16px] font-semibold text-inchiostro/70">{t('nav.pro')}</p>
            <ul className="mb-10">
              {b2b.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    data-testid={`m-pro-nav-${l.to.split('/').pop()}`}
                    className="flex items-center justify-between py-3 text-[18px] font-semibold"
                  >
                    {l.label}
                    <ChevronRight size={16} className="text-marsala" strokeWidth={1.4} />
                  </Link>
                </li>
              ))}
            </ul>

            <div className="flex items-center justify-between">
              <Link to="/dove-comprare" data-testid="m-cta-where" className="btn btn-solid">
                <MapPin size={13} /> {t('common.findRetailer')}
              </Link>
              <LangSwitch lang={lang} setLang={setLang} testid="lang-switch-panel" />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

/** Il punto che dice "e' adesso": la stagione in corso, non un ornamento. */
const Acceso = () => (
  <span
    aria-hidden
    data-testid="nav-stagione-accesa"
    className="mr-2 inline-block h-[6px] w-[6px] rounded-full bg-ocra align-middle"
  />
);

export const LangSwitch = ({ lang, setLang, light = false, testid = 'lang-switch' }) => {
  const { t } = useLang();
  return (
    <div
      className="flex items-center gap-1 text-[14px] font-bold uppercase tracking-[0.18em]"
      data-testid={testid}
    >
      {['it', 'en'].map((l) => (
        <button
          key={l}
          type="button"
          lang={l}
          onClick={() => setLang(l)}
          data-testid={`${testid}-${l}`}
          aria-pressed={lang === l}
          aria-label={l === 'it' ? t('common.languageIt') : t('common.languageEn')}
          className={`inline-flex min-h-[44px] min-w-[44px] items-center justify-center px-2 py-1 transition-colors ${
            lang === l
              ? light
                ? 'text-ocra'
                : 'text-marsala'
              : light
                ? 'text-carta/50 hover:text-carta'
                : 'text-inchiostro/65 hover:text-inchiostro'
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
};
